import { useEffect, useState } from 'react';
import { supabase } from '../supabase';
import { loadLocalTeam } from '../localTeam';

/**
 * The full team row the Season planner + weekly batch generator need —
 * season dates, training cadence, format. `useActiveTeam` deliberately
 * returns only id/name/age_group for light surfaces; this is the heavier
 * read for planning. Column set mirrors the Brain `get_team` tool
 * (brainTools.ts) so both paths see the same shape.
 */
export interface TeamFull {
  id: string;
  name: string | null;
  age_group: string | null;
  proficiency_level: string | null;
  formation_default: string | null;
  season_type: string | null;
  season_start: string | null;
  season_end: string | null;
  training_days_per_week: number | null;
  session_duration_mins: number | null;
  player_count: number | null;
}

const TEAM_COLUMNS =
  'id, name, age_group, proficiency_level, formation_default, season_type, ' +
  'season_start, season_end, training_days_per_week, session_duration_mins, player_count';

/**
 * Resolve the coach's active team with the full planning column set.
 * Supabase first; falls back to the localStorage team Onboarding saves
 * when the DB is unreachable (the rest of the fields come back null).
 */
export function useTeamFull() {
  const [team, setTeam] = useState<TeamFull | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await supabase
          .from('teams')
          .select(TEAM_COLUMNS)
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (data) {
          // The concatenated column string defeats the client's literal-select
          // type inference, so widen through unknown.
          setTeam(data as unknown as TeamFull);
          setLoading(false);
          return;
        }
      } catch {
        // fall through to local fallback
      }
      if (cancelled) return;
      const local = loadLocalTeam();
      setTeam(
        local
          ? {
              id: local.id,
              name: local.name,
              age_group: local.age_group,
              proficiency_level: null,
              formation_default: null,
              season_type: null,
              season_start: null,
              season_end: null,
              training_days_per_week: null,
              session_duration_mins: null,
              player_count: null,
            }
          : null,
      );
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  return { team, loading };
}
